import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Swal from "sweetalert2";

import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button
} from "reactstrap";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchPromoCodesData } from "../../store/promo-codes/actions";

const PromoCodes = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, loading, error } = useSelector(state => state.PromoCodes);

  const [viewModal, setViewModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);

  useEffect(() => {
    dispatch(fetchPromoCodesData());
  }, [dispatch]);

  const handleView = (promo) => {
    setSelectedPromo(promo);
    setViewModal(true);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `${process.env.REACT_APP_WEB_APP_URL}/api/web/promo-codes/delete/${id}`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (response.status === 200) {
            toast.success('Promo Code has been deleted.', { autoClose: 3000 });
            dispatch(fetchPromoCodesData());
          } else {
            toast.error(result?.message || 'Failed to delete', { autoClose: 3000 });
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to delete promo code", { autoClose: 3000 });
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: "Image",
        accessorKey: "image",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <img
            src={row.original.image || "assets/images/no-image.png"}
            alt="Image"
            style={{ width: 40, height: 40, borderRadius: "50%" }}
          />
        )
      },
      {
        header: 'Name',
        accessorKey: 'name',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Code',
        accessorKey: 'code',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Start Date',
        accessorKey: 'start_date',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'End Date',
        accessorKey: 'end_date',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Type',
        accessorKey: 'type',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Value',
        accessorKey: 'value',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Action',
        id: 'action',
        cell: ({ row }) => {
          const id = row.original?.id;
          const is_deleted = row.original?.is_deleted;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-info"
                onClick={() => handleView(row.original)}
              >
                <i className="mdi mdi-eye"></i> View
              </button>
              {is_deleted == "Yes" ? (
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => handleRestore(id)}
                >
                  <i className="mdi mdi-restore"></i> Restore
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(id)}
                >
                  <i className="mdi mdi-delete"></i> Delete
                </button>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const totalRecords = data?.data?.length ?? 0;

  const transformedData = useMemo(() => {
    return data?.data?.map(item => {
      return {
        id: item.id,
        image: item.image || "assets/images/no-image.png",
        name: item.name || "-",
        code: item.code || "-",
        start_date: item.start_date || "-",
        end_date: item.end_date || "-",
        type: item.type || "-",
        value: item.value || "-",
        status: item.status || "-",
        ...item
      };
    });
  }, [data]);

  document.title = "Promo Codes | Moeen";
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Promo Codes" />
        <div className="float-end d-none d-md-block">
          <Link to="/promo-codes/create"
            color="primary"
            className="btn btn-primary text-white waves-effect waves-light"
          >
            Add Promo Code
          </Link>
        </div>
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            isCustomPageSize={true}
            SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        )}

        {/* View Modal */}
        <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg" centered>
          <ModalHeader toggle={() => setViewModal(false)}>
            Promo Code Details
          </ModalHeader>
          <ModalBody>
            {selectedPromo ? (
              <div className="table-responsive">
                <table className="table table-striped">
                  <tbody>
                    <tr><th>ID</th><td>{selectedPromo.id}</td></tr>
                    <tr><th>Name</th><td>{selectedPromo.name}</td></tr>
                    <tr><th>Code</th><td>{selectedPromo.code}</td></tr>
                    <tr><th>Type</th><td>{selectedPromo.type}</td></tr>
                    <tr><th>Value</th><td>{selectedPromo.value}</td></tr>
                    <tr><th>Scope</th><td>{selectedPromo.voucher_scope}</td></tr>
                    <tr><th>For</th><td>{selectedPromo.voucher_for || "-"}</td></tr>
                    <tr><th>Count</th><td>{selectedPromo.count}</td></tr>
                    <tr><th>Status</th><td>{selectedPromo.status}</td></tr>
                    <tr><th>Start Date</th><td>{selectedPromo.start_date}</td></tr>
                    <tr><th>End Date</th><td>{selectedPromo.end_date}</td></tr>
                    <tr><th>Created At</th><td>{selectedPromo.created_at}</td></tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No promo selected.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setViewModal(false)}>Close</Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

PromoCodes.propTypes = {
  t: PropTypes.any
};

export default PromoCodes;